import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BookOpen, ArrowRight, PlayCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import axios from 'axios';
import { UserContext } from '@/lib/User';
import CourseCard from '@/components/shared/CourseCard';

export default function Dashboard() {
  const { user } = useContext(UserContext);
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allCourses, setAllCourses] = useState([]);

  const getUserDetails = async () => {
    try {
      const res = await axios.post(
        "https://xceededge.com/get-user-details.php",
        { session: user?.session },
        { headers: { "Content-Type": "application/json" } }
      );

      if (res?.data?.success) {
        setUserDetails(res.data);
      }
    } catch (error) {
      console.error("Failed to fetch user details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.session) {
      getUserDetails();
    }
  }, [user]);

  // Recommendations come from the public catalogue (same endpoint as /courses),
  // ordered by popularity; enrolled courses are filtered out below.
  useEffect(() => {
    const formData = new FormData();
    formData.append('action', 'filter');
    formData.append('sort', 'popular');
    axios.post('https://xceededge.com/courses.php', formData)
      .then((res) => {
        if (res.data?.success) setAllCourses(res.data.data || []);
      })
      .catch((error) => console.error('Failed to fetch recommended courses:', error));
  }, []);

  const enrolled = userDetails?.enrolled_courses || [];
  const totalEnrolled = userDetails?.total_enrolled || 0;

  // Order enrolled courses by progress (highest first) for the Continue Learning list.
  const continueCourses = [...enrolled].sort((a, b) => (b.progress || 0) - (a.progress || 0));

  const enrolledIds = new Set(enrolled.map((c) => String(c.course_id)));
  const recommended = allCourses
    .filter((c) => !enrolledIds.has(String(c.id)))
    .slice(0, 3);

  // Enrolled Courses is the only stat backed by real data, so it sits inside the
  // welcome banner rather than alone in a stat grid. Hours Learned, Certificates
  // and Avg Progress were removed: Hours Learned was a made-up
  // `enrolled.length * 12`, and nothing writes `enrollments.progress`, so Avg
  // Progress (and Certificates, derived from progress === 100) could only ever
  // render 0.

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-8">
      {/* Welcome Section — carries the enrolled-courses stat so a single figure
          doesn't sit alone in an otherwise empty stat grid. */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass rounded-3xl border border-white/80 p-7 shadow-sm relative overflow-hidden"
      >
        <div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-10 pointer-events-none"
          style={{ background: 'radial-gradient(circle, #FED202 0%, transparent 70%)', transform: 'translate(30%, -30%)' }} />

        <div className="relative flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">
              Welcome back, <span className="text-primary">{userDetails?.user?.full_name || user?.name}</span>! 👋
            </h1>
            <p className="text-muted-foreground mt-2 font-body text-base">
              {totalEnrolled > 0
                ? 'Ready to continue your learning journey?'
                : 'Browse our courses and start your learning journey today.'}
            </p>
          </div>

          {/* Enrolled courses — the one figure on this page backed by real data */}
          <div className="flex items-center gap-4 rounded-2xl border border-white/80 bg-white/70 px-5 py-4 shadow-sm backdrop-blur-sm sm:flex-shrink-0">
            <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-primary/15">
              <BookOpen className="h-5 w-5 text-primary" />
            </span>
            <span className="min-w-0">
              <span className="block font-heading text-3xl font-bold leading-none tracking-tight">
                {totalEnrolled}
              </span>
              <span className="mt-1 block font-body text-xs text-muted-foreground">
                {totalEnrolled === 1 ? 'Enrolled Course' : 'Enrolled Courses'}
              </span>
            </span>
          </div>
        </div>
      </motion.div>

      {/* Continue Learning — one row per enrolled course */}
      {continueCourses.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="glass rounded-2xl border border-white/80 p-6 shadow-sm"
        >
          <h2 className="font-heading font-bold text-lg mb-4 tracking-tight">Continue Learning</h2>

          <div className="space-y-4">
            {continueCourses.map((c) => (
              <div
                key={c.course_url || c.id || c.title}
                className="flex flex-col sm:flex-row gap-4 items-start sm:items-center p-3 rounded-xl border border-border/60 bg-white/40 hover:bg-white/70 transition"
              >
                <div className="w-16 h-16 rounded-2xl bg-primary/15 flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {c.image_url ? (
                    <img src={c.image_url} alt={c.title} className="w-full h-full object-cover" />
                  ) : (
                    <PlayCircle className="w-8 h-8 text-primary" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <h3 className="font-heading font-semibold truncate">{c.title}</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {c.instructor_name}
                  </p>

                  {/* <div className="flex items-center gap-3 mt-3">
                    <Progress value={c.progress || 0} className="flex-1 h-1.5" />
                    <span className="text-sm font-semibold text-foreground/70 shrink-0">
                      {c.progress || 0}%
                    </span>
                  </div> */}
                </div>

                <Link to={`/dashboard/${c.course_url || c.id}`}>
                  <Button className="gap-2 rounded-xl bg-primary text-primary-foreground shadow-md shadow-primary/20 font-semibold shrink-0">
                    Continue <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Recommended Courses — popular catalogue courses the user isn't enrolled in */}
      {recommended.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="glass rounded-2xl border border-white/80 p-6 shadow-sm"
        >
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-heading font-bold text-lg tracking-tight">Recommended for You</h2>
            <Link to="/courses">
              <Button variant="ghost" size="sm" className="gap-1 text-primary font-medium rounded-xl hover:bg-primary/10">
                View All <ArrowRight className="w-3.5 h-3.5" />
              </Button>
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {recommended.map((course, i) => (
              <CourseCard key={course.id} course={course} index={i} />
            ))}
          </div>
        </motion.div>
      )}

      {/* WhatsApp Floating Button */}
      <motion.a
        href="https://api.whatsapp.com/send/?phone=919391650077&text=Hi+I+want+a+Free+Career+Plan+Session&type=phone_number&app_absent=0"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat with us on WhatsApp for a free career plan session"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.95 }}
        transition={{ delay: 0.6 }}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#25D366] text-white shadow-lg shadow-[#25D366]/30 flex items-center justify-center hover:bg-[#1ebe57] transition-colors"
      >
        <svg viewBox="0 0 32 32" className="w-7 h-7" fill="currentColor" aria-hidden="true">
          <path d="M16.003 3C9.374 3 4 8.373 4 14.998c0 2.367.687 4.57 1.873 6.43L4 29l7.79-1.836a11.94 11.94 0 0 0 4.213.76h.001C22.633 27.924 28 22.55 28 15.926 28 12.713 26.748 9.694 24.475 7.42 22.2 5.146 19.18 3.93 16.003 3.93Zm0 1.86c2.7 0 5.236 1.05 7.144 2.957a10.08 10.08 0 0 1 2.961 7.142c0 5.572-4.534 10.103-10.105 10.103h-.001a10.04 10.04 0 0 1-3.797-.745l-.273-.108-3.96.93.933-3.86-.122-.282a10 10 0 0 1-1.55-5.367c0-5.572 4.535-10.104 10.107-10.104Zm-2.13 4.43c-.197 0-.518.074-.79.366-.272.291-1.04 1.015-1.04 2.476 0 1.46 1.064 2.872 1.213 3.07.148.197 2.073 3.317 5.124 4.514 2.534.994 3.05.797 3.6.747.552-.05 1.78-.728 2.03-1.43.25-.703.25-1.305.176-1.43-.074-.124-.272-.198-.57-.347-.297-.148-1.78-.878-2.055-.978-.273-.099-.472-.148-.67.149-.198.297-.768.978-.94 1.18-.174.198-.347.223-.643.075-.297-.149-1.258-.464-2.396-1.48-.886-.79-1.484-1.766-1.657-2.064-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.149-.173.198-.297.297-.495.099-.198.05-.371-.025-.52-.074-.148-.668-1.612-.916-2.207-.24-.578-.487-.5-.67-.51l-.572-.01Z"/>
        </svg>
      </motion.a>
    </motion.div>
  );
}